import { ERROR_CODES } from '@mano/shared';
import type { ApiErrorBody, ErrorCode } from '@mano/shared';
import type { MessageKey } from '../i18n/messages/pt-BR';

/** Client-only codes (no server body): network failure, or a non-JSON / unknown error body. */
export type ClientErrorCode = ErrorCode | 'network' | 'unknown';

export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: ClientErrorCode;
  readonly details: unknown;
  constructor(status: number, code: ClientErrorCode, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/** Codes the server may send (single source: ERROR_CODES in @mano/shared). */
const KNOWN_CODES = new Set<string>(ERROR_CODES);

export function isKnownErrorCode(code: unknown): code is ErrorCode {
  return typeof code === 'string' && KNOWN_CODES.has(code);
}

function isErrorBody(body: unknown): body is ApiErrorBody {
  if (!body || typeof body !== 'object') return false;
  const e = (body as { error?: unknown }).error;
  return !!e && typeof e === 'object' && typeof (e as { code?: unknown }).code === 'string';
}

/** Status-only fallback when the body is missing or not ours (proxy pages, 502s…). */
export function codeForStatus(status: number): ClientErrorCode {
  if (status === 0) return 'network';
  if (status === 400) return 'bad_request';
  if (status === 401) return 'unauthorized';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not_found';
  if (status === 409) return 'conflict';
  if (status === 429) return 'rate_limited';
  if (status >= 500) return 'internal';
  return 'unknown';
}

/** Builds a typed error from an HTTP response status + parsed body (body may be anything). */
export function toApiError(status: number, body: unknown): ApiRequestError {
  if (isErrorBody(body)) {
    const code = isKnownErrorCode(body.error.code) ? body.error.code : codeForStatus(status);
    return new ApiRequestError(status, code, body.error.message || code, body.error.details);
  }
  const code = codeForStatus(status);
  return new ApiRequestError(status, code, `HTTP ${status}`);
}

/** i18n key for any thrown value. Unknown errors map to a generic, friendly message. */
export function errorMessageKey(err: unknown): MessageKey {
  if (err instanceof ApiRequestError) return `errors.${err.code}` as MessageKey;
  if (err instanceof TypeError) return 'errors.network';
  return 'errors.unknown';
}

export function errorCode(err: unknown): ClientErrorCode | null {
  return err instanceof ApiRequestError ? err.code : null;
}
